import React, { useState, useEffect } from 'react';
import { securityAuditLogger } from '../../utils/securityAuditLogger';
import { tokenManager } from '../../utils/tokenManager';
import { Card } from '../common/Card';
import { Button } from '../common/Button';

interface SecurityEvent {
  type: string;
  timestamp: string;
  data?: Record<string, any>;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  userId?: string;
  sessionId?: string;
}

export const SecurityDashboard: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<SecurityEvent[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [tokenInfo, setTokenInfo] = useState<any>(null);
  const [filter, setFilter] = useState<{
    type?: string;
    severity?: string;
    startDate?: string;
    endDate?: string;
  }>({});

  useEffect(() => {
    loadSecurityData();
  }, [filter]);

  const loadSecurityData = () => {
    // Load audit logs with filter
    const logs = securityAuditLogger.getAuditLogs({
      type: filter.type || undefined,
      severity: filter.severity as any,
      startDate: filter.startDate ? new Date(filter.startDate) : undefined,
      endDate: filter.endDate ? new Date(filter.endDate) : undefined,
    });
    
    setAuditLogs(logs);
    setStats(securityAuditLogger.getSecurityStats());
    setTokenInfo(tokenManager.getTokenInfo());
  };

  const handleExportLogs = (format: 'json' | 'csv') => {
    const exportData = securityAuditLogger.exportAuditLogs(format);
    const blob = new Blob([exportData], { 
      type: format === 'json' ? 'application/json' : 'text/csv' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `security-audit-${new Date().toISOString().split('T')[0]}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleForceLogout = () => {
    if (confirm('Are you sure you want to force logout? This will end your session immediately.')) {
      tokenManager.forceSessionTimeout();
    }
  };

  const getSeverityColor = (severity?: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Security Dashboard</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExportLogs('json')}
            size="sm"
          >
            Export JSON
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExportLogs('csv')}
            size="sm"
          >
            Export CSV
          </Button>
          <Button
            variant="danger"
            onClick={handleForceLogout}
            size="sm"
          >
            Force Logout
          </Button>
        </div>
      </div>

      {/* Security Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-2">Total Events</h3>
            <p className="text-3xl font-bold text-blue-600">{stats.totalEvents}</p>
          </Card>
          
          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-2">Recent Events (24h)</h3>
            <p className="text-3xl font-bold text-green-600">{stats.recentEvents.length}</p>
          </Card>
          
          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-2">Critical Events</h3>
            <p className="text-3xl font-bold text-red-600">
              {stats.eventsBySeverity.critical || 0}
            </p>
          </Card>
        </div>
      )}

      {/* Token Information */}
      {tokenInfo && (
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Token Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Issued At</p>
              <p className="font-medium">{formatTimestamp(new Date(tokenInfo.issuedAt).toISOString())}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Expires At</p>
              <p className="font-medium">{formatTimestamp(new Date(tokenInfo.expiresAt).toISOString())}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Has Refresh Token</p>
              <p className="font-medium">{tokenInfo.refreshToken ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Session Timeout</p>
              <p className="font-medium">
                {tokenManager.isSessionTimedOut() ? 'Expired' : 'Active'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Type
            </label>
            <select
              value={filter.type || ''}
              onChange={(e) => setFilter({ ...filter, type: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="auth_login">Login</option>
              <option value="auth_logout">Logout</option>
              <option value="auth_register">Register</option>
              <option value="token_refresh_success">Token Refresh</option>
              <option value="authentication_failure">Auth Failure</option>
              <option value="session_timeout">Session Timeout</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Severity
            </label>
            <select
              value={filter.severity || ''}
              onChange={(e) => setFilter({ ...filter, severity: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Severities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filter.startDate || ''}
              onChange={(e) => setFilter({ ...filter, startDate: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filter.endDate || ''}
              onChange={(e) => setFilter({ ...filter, endDate: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </Card>

      {/* Audit Logs */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-4">Security Audit Logs</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {auditLogs.slice(0, 50).map((log, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatTimestamp(log.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {log.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(log.severity)}`}>
                      {log.severity || 'unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.userId || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <details className="cursor-pointer">
                      <summary className="font-medium">View Details</summary>
                      <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                        {JSON.stringify(log.data, null, 2)}
                      </pre>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {auditLogs.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No security events found matching the current filters.
            </div>
          )}
          
          {auditLogs.length > 50 && (
            <div className="text-center py-4 text-gray-500">
              Showing first 50 events. Use filters to narrow results or export for full data.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};